/**
 * MODEL CATALOG STATE MANAGEMENT WITH JOTAI
 *
 * Centralized atom-based state management for the model catalog.
 * This replaces the singleton pattern with reactive atoms.
 */

import React from 'react'
import { atom, useAtomValue } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { ModelCatalogService } from '../../../../shared-model-catalog'
import type {
  ModelCatalogEntry,
  ModelQueryFilter,
  ModelSortOptions,
  ModelTierAvailability,
} from '../../../../shared-model-catalog'
import type { ProviderTier } from '../../../../shared-pricing-config'

export interface ModelUptime {
  modelId: string
  segments: Array<{
    start: number
    successCount: number
    serverErrorCount: number
    excludedClientErrorCount: number
  }>
}

interface ModelUptimeResponse {
  models: Array<ModelUptime>
  segmentMinutes: number
  windowHours: number
}

// ========================================
// ATOMS
// ========================================

/**
 * Atom that fetches tier availability from API and caches it.
 * Automatically refetches every 5 minutes.
 */
export const tierAvailabilityAtom = atomWithQuery(() => ({
  queryKey: ['model-tier-availability'],
  queryFn: async (): Promise<Array<ModelTierAvailability>> => {
    const response = await fetch('/api/models/tier-availability')
    if (!response.ok) {
      throw new Error('Failed to fetch tier availability')
    }
    return response.json()
  },
  staleTime: 5 * 60 * 1000, // 5 minutes
  refetchOnWindowFocus: false,
}))

/** Observed rolling 24-hour availability in 30-minute segments for catalog cards. */
export const modelUptimeAtom = atomWithQuery(() => ({
  queryKey: ['model-uptime'],
  queryFn: async (): Promise<ModelUptimeResponse> => {
    const response = await fetch('/api/models/uptime')
    if (!response.ok) {
      throw new Error('Failed to fetch model uptime')
    }
    return response.json()
  },
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
}))

/**
 * Derived atom that creates a ModelCatalogService instance
 * initialized with the tier availability data.
 */
export const modelCatalogAtom = atom((get) => {
  const tierData = get(tierAvailabilityAtom)

  // Return catalog with data if loaded, otherwise empty catalog
  if (tierData.data) {
    return new ModelCatalogService(tierData.data)
  }

  // Return empty catalog while loading
  return new ModelCatalogService([])
})

/**
 * Atom to track if catalog is loading
 */
export const catalogLoadingAtom = atom((get) => {
  const tierData = get(tierAvailabilityAtom)
  return tierData.isLoading || tierData.isPending
})

/**
 * Atom to track catalog errors
 */
export const catalogErrorAtom = atom((get) => {
  const tierData = get(tierAvailabilityAtom)
  return tierData.error
})

// ========================================
// HOOKS
// ========================================

/**
 * Hook to access the model catalog service.
 * Automatically subscribes to tier availability updates.
 */
export function useModelCatalog(): ModelCatalogService {
  return useAtomValue(modelCatalogAtom)
}

/**
 * Hook to check if catalog is loading
 */
export function useCatalogLoading(): boolean {
  return useAtomValue(catalogLoadingAtom)
}

/**
 * Hook to get catalog error if any
 */
export function useCatalogError(): Error | null {
  return useAtomValue(catalogErrorAtom)
}

/**
 * Hook to query models with filters and sorting.
 * Automatically updates when catalog data changes.
 */
export function useQueryModels(
  filter?: ModelQueryFilter,
  sort?: ModelSortOptions,
): Array<ModelCatalogEntry> {
  const catalog = useModelCatalog()

  // Use useMemo to avoid recalculating on every render
  return React.useMemo(
    () => catalog.listModels(filter, sort),
    [catalog, filter, sort],
  )
}

/**
 * Hook to get models available for specific tiers.
 */
export function useModelsForTiers(
  tiers: Array<ProviderTier>,
): Array<ModelCatalogEntry> {
  const catalog = useModelCatalog()

  return React.useMemo(() => catalog.getModelsForTiers(tiers), [catalog, tiers])
}

/**
 * Hook to get a single model by ID.
 */
export function useModel(modelId: string): ModelCatalogEntry | undefined {
  const catalog = useModelCatalog()

  return React.useMemo(() => catalog.getModel(modelId), [catalog, modelId])
}

/**
 * Hook to check if model is available on a tier.
 */
export function useModelTierAvailability(
  modelId: string,
  tier: ProviderTier,
): boolean {
  const catalog = useModelCatalog()

  return React.useMemo(
    () => catalog.isModelAvailableOnTier(modelId, tier),
    [catalog, modelId, tier],
  )
}

/**
 * Hook to get all available tiers for a model.
 */
export function useModelTiers(modelId: string): Array<ProviderTier> {
  const catalog = useModelCatalog()

  return React.useMemo(
    () => catalog.getAvailableTiers(modelId),
    [catalog, modelId],
  )
}

/**
 * Get observed uptime by model ID. Models without requests are absent; cards
 * render those hours as operational to match the uptime policy.
 */
export function useModelUptimeMap(): ReadonlyMap<string, ModelUptime> {
  const uptime = useAtomValue(modelUptimeAtom)

  return React.useMemo(
    () => new Map(uptime.data?.models.map((model) => [model.modelId, model])),
    [uptime.data],
  )
}

/**
 * Hook to manually refetch tier availability data.
 */
export function useRefetchCatalog() {
  // Jotai's atomWithQuery automatically handles refetching
  // This is a no-op placeholder for API compatibility
  return () => {
    // The query will refetch automatically when stale
    console.log('Catalog will refetch automatically when stale')
  }
}
